/**
 * NativeValidationTranslator
 * Puts the browser's own validation bubbles («Please fill out this field.») in the app's language.
 *
 * One listener for the whole app, mounted in `AppProviders`: when a control fires `invalid` the message is
 * replaced through `setCustomValidity` with the catalogue text (`validation:native.*`), and cleared as soon
 * as the user edits the field so the browser re-validates natively. Forms that validate with zod and set
 * `noValidate` never fire `invalid`, so they are untouched; a control can opt out with
 * `data-native-validation="off"`, and a message the app set itself is never overwritten.
 */
import React, { useEffect, useRef } from 'react'

import { useTranslation } from '@/platform/i18n/hooks/useTranslation'
import {
  describeNativeValidity,
  type ValidatableControl,
} from '@/shared/utils/native-validation-message'

type Control = (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) & ValidatableControl

const isControl = (target: EventTarget | null): target is Control =>
  target instanceof HTMLInputElement ||
  target instanceof HTMLSelectElement ||
  target instanceof HTMLTextAreaElement

export const NativeValidationTranslator: React.FC = () => {
  const { t } = useTranslation('validation')
  // The listeners are installed once; they read the CURRENT translator (language can change at runtime).
  const translate = useRef(t)
  translate.current = t

  useEffect(() => {
    const ours = new WeakSet<Control>()

    const onInvalid = (event: Event) => {
      const control = event.target
      if (!isControl(control) || control.dataset['nativeValidation'] === 'off') return
      // A message we set earlier is re-derived (the language may have changed since); anyone else's stays.
      if (ours.has(control)) control.setCustomValidity('')
      const message = describeNativeValidity(control)
      if (!message) return
      control.setCustomValidity(translate.current(message.key, message.values))
      ours.add(control)
    }

    const onEdit = (event: Event) => {
      const control = event.target
      if (isControl(control) && ours.has(control)) {
        control.setCustomValidity('')
        ours.delete(control)
      }
    }

    // Capture phase: `invalid` does not bubble.
    document.addEventListener('invalid', onInvalid, true)
    document.addEventListener('input', onEdit, true)
    document.addEventListener('change', onEdit, true)
    return () => {
      document.removeEventListener('invalid', onInvalid, true)
      document.removeEventListener('input', onEdit, true)
      document.removeEventListener('change', onEdit, true)
    }
  }, [])

  return null
}
